import React, { useState, useRef } from "react";
import "./App.css";

const STATUS_CONFIG = {
  DISCONNECTED: { bg: "#1e293b", label: "DISCONNECTED", icon: "○", textColor: "#94a3b8", pulse: false },
  CONNECTING:   { bg: "#b45309", label: "CONNECTING...", icon: "◌", textColor: "#ffffff", pulse: true  },
  CONNECTED:    { bg: "#1d4ed8", label: "CONNECTED",    icon: "●", textColor: "#ffffff", pulse: false },
  OPEN:         { bg: "#b91c1c", label: "OPEN",         icon: "✕", textColor: "#ffffff", pulse: false },
  CLOSED:       { bg: "#15803d", label: "CLOSED",       icon: "✓", textColor: "#ffffff", pulse: false },
  ERROR:        { bg: "#7f1d1d", label: "ERROR",        icon: "!", textColor: "#fca5a5", pulse: false },
};

function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.6, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.6);
  } catch (e) {
    console.warn("Audio not available:", e);
  }
}

function playAlert() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // Three sharp descending beeps at full volume
    const pattern = [
      { freq: 1400, start: 0.0,  end: 0.18 },
      { freq: 1200, start: 0.22, end: 0.40 },
      { freq: 1000, start: 0.44, end: 0.65 },
    ];
    pattern.forEach(({ freq, start, end }) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "square";
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      gain.gain.setValueAtTime(0.0,  ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(1.0,  ctx.currentTime + start + 0.01);
      gain.gain.setValueAtTime(1.0,  ctx.currentTime + end - 0.02);
      gain.gain.linearRampToValueAtTime(0.0,  ctx.currentTime + end);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + end);
    });
    // Vibrate: long-short-long pattern (ms on, ms off, ...)
    if (navigator.vibrate) {
      navigator.vibrate([300, 100, 300, 100, 500]);
    }
  } catch (e) {
    console.warn("Alert not available:", e);
  }
}

function App() {
  const [status, setStatus] = useState("DISCONNECTED");
  const [message, setMessage] = useState("");
  const [deviceInfo, setDeviceInfo] = useState(null);   // { name, id }
  const [messageLog, setMessageLog] = useState([]);     // last 10 BLE messages
  const [inputs, setInputs] = useState([0, 0, 0, 0]);   // 4 GPIO input states (pins 18,19,21,22)
  const lastStatusRef   = useRef("DISCONNECTED");
  const deviceRef       = useRef(null);   // saved BluetoothDevice
  const reconnectingRef        = useRef(false); // guard against parallel reconnect loops
  const intentionalDisconnectRef = useRef(false); // true when user clicked Disconnect
  const charRef                  = useRef(null);  // active BLE characteristic
  const notifyHandlerRef         = useRef(null); // current notification handler

  const SERVICE_UUID = "4fafc201-1fb5-459e-8fcc-c5c9c331914b";
  const CHAR_UUID    = "beb5483e-36e1-4688-b7f5-ea07361b26a8";

  const isConnected = ["CONNECTING", "CONNECTED", "OPEN", "CLOSED"].includes(status);

  // Attach notifications to the ESP32 characteristic and parse 4-byte payload
  const attachNotifications = async (server) => {
    const service = await server.getPrimaryService(SERVICE_UUID);
    const char = await service.getCharacteristic(CHAR_UUID);

    // Clean up any previous listener before re-attaching
    if (charRef.current && notifyHandlerRef.current) {
      charRef.current.removeEventListener("characteristicvaluechanged", notifyHandlerRef.current);
    }

    const handler = (event) => {
      const val = event.target.value; // DataView, 4 bytes
      const bytes = [val.getUint8(0), val.getUint8(1), val.getUint8(2), val.getUint8(3)];
      setInputs(bytes);
      // GPIO 18 (inputs[0]) is the primary sensor — also mirrors the onboard LED
      const newStatus = bytes[0] === 1 ? "CLOSED" : "OPEN";
      if (newStatus === "OPEN" && lastStatusRef.current !== "OPEN") {
        playAlert();
      }
      lastStatusRef.current = newStatus;
      setStatus(newStatus);
      const msg = `[${bytes.join(", ")}]`;
      setMessage(msg);
      setMessageLog(prev =>
        [`${new Date().toLocaleTimeString()} → ${msg}`, ...prev].slice(0, 10)
      );
    };

    notifyHandlerRef.current = handler;
    charRef.current = char;
    await char.startNotifications();
    char.addEventListener("characteristicvaluechanged", handler);
  };

  // Reconnect loop — retries every 3 s until successful
  const reconnect = async (device) => {
    if (reconnectingRef.current) return;
    reconnectingRef.current = true;
    setStatus("CONNECTING");
    setMessage("");

    while (reconnectingRef.current) {
      try {
        console.log("Attempting reconnect…");
        const server = await device.gatt.connect();
        await attachNotifications(server);
        playBeep();
        setStatus("CONNECTED");
        reconnectingRef.current = false;
        return;
      } catch (err) {
        console.warn("Reconnect failed, retrying in 3 s…", err);
        await new Promise((res) => setTimeout(res, 3000));
      }
    }
  };

  const disconnectBLE = () => {
    intentionalDisconnectRef.current = true; // prevent gattserverdisconnected from triggering reconnect
    reconnectingRef.current = false;
    if (charRef.current && notifyHandlerRef.current) {
      charRef.current.removeEventListener("characteristicvaluechanged", notifyHandlerRef.current);
      charRef.current = null;
      notifyHandlerRef.current = null;
    }
    if (deviceRef.current?.gatt?.connected) {
      deviceRef.current.gatt.disconnect();
    }
    deviceRef.current = null;
    setStatus("DISCONNECTED");
    setMessage("");
    setDeviceInfo(null);
    setMessageLog([]);
    setInputs([0, 0, 0, 0]);
    lastStatusRef.current = "DISCONNECTED";
  };

  const connectBLE = async () => {
    try {
      setStatus("CONNECTING");
      setMessage("");
      reconnectingRef.current = false;
      intentionalDisconnectRef.current = false;

      const device = await navigator.bluetooth.requestDevice({
        filters: [{ name: "Flinker-Trailer" }],
        optionalServices: [SERVICE_UUID],
      });

      deviceRef.current = device;
      setDeviceInfo({ name: device.name || "Unknown", id: device.id });

      // Listen for disconnection
      device.addEventListener("gattserverdisconnected", () => {
        if (intentionalDisconnectRef.current) {
          intentionalDisconnectRef.current = false;
          return; // user clicked Disconnect — do not reconnect
        }
        console.log("Device disconnected — starting auto-reconnect");
        setStatus("CONNECTING");
        reconnect(device);
      });

      const server = await device.gatt.connect();
      await attachNotifications(server);
      playBeep();
      setStatus("CONNECTED");
    } catch (error) {
      console.error(error);
      setStatus("ERROR");
    }
  };

  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.DISCONNECTED;

  return (
    <div
      className="app-bg"
      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/bg.jpg)` }}
    >
      <div className="app-overlay">
        <div className="app-content">

          {/* Logo & Brand */}
          <div className="brand-section">
            <img
              src={process.env.PUBLIC_URL + "/flinker-logo.jpg"}
              alt="Flinker"
              className="brand-logo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <span className="brand-name">FLINKER</span>
          </div>

          <p className="brand-subtitle">BLE Trunk Monitor</p>

          {/* Status Card */}
          <div
            className={`status-card${cfg.pulse ? " status-card--pulse" : ""}`}
            style={{ backgroundColor: cfg.bg }}
          >
            <span className="status-icon" style={{ color: cfg.textColor }}>
              {cfg.icon}
            </span>
            <span className="status-label" style={{ color: cfg.textColor }}>
              {cfg.label}
            </span>
            {cfg.pulse && <div className="status-progress-bar" />}
          </div>

          {/* Raw BLE Message */}
          {message && (
            <div className="message-box">
              <span className="message-header">Raw BLE Message</span>
              <code className="message-value">{message}</code>
            </div>
          )}

          {/* 4-Input Status Grid */}
          {["CONNECTED", "OPEN", "CLOSED"].includes(status) && (
            <div className="inputs-panel">
              <span className="inputs-header">Input Status (GPIO)</span>
              <div className="inputs-grid">
                {[18, 19, 21, 22].map((pin, i) => (
                  <div
                    key={pin}
                    className={`input-indicator${inputs[i] === 1 ? " input-indicator--active" : ""}`}
                  >
                    <span className="input-dot" />
                    <span className="input-label">GPIO {pin}</span>
                    <span className="input-value">{inputs[i] === 1 ? "ON" : "OFF"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Connect / Disconnect Buttons */}
          {!isConnected && (
            <button
              className="connect-btn"
              onClick={connectBLE}
              disabled={status === "CONNECTING"}
            >
              {status === "CONNECTING" ? "Connecting…" : "Connect Device"}
            </button>
          )}

          {isConnected && status !== "CONNECTING" && (
            <button className="disconnect-btn" onClick={disconnectBLE}>
              Disconnect
            </button>
          )}

          {/* Debug Panel */}
          {deviceInfo && (
            <div className="debug-panel">
              <div className="debug-header">Debug Info</div>
              <div className="debug-row">
                <span className="debug-label">Device Name</span>
                <span className="debug-value">{deviceInfo.name}</span>
              </div>
              <div className="debug-row">
                <span className="debug-label">Device ID</span>
                <span className="debug-value debug-id">{deviceInfo.id}</span>
              </div>
              <div className="debug-row">
                <span className="debug-label">Connection</span>
                <span className="debug-value">{status}</span>
              </div>
              {messageLog.length > 0 && (
                <div className="debug-log-section">
                  <span className="debug-label">Message Log (last 10)</span>
                  <ul className="debug-log">
                    {messageLog.map((entry, i) => (
                      <li key={i} className="debug-log-entry">{entry}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default App;